/**
 * Normalize API / store error shapes into readable strings and Error instances.
 *
 * Handles objects thrown by request.js ({ status, error: { errors: [...] } }),
 * re-thrown err.error payloads, and Error instances created with object messages.
 */

const isNonEmptyString = value => typeof value === 'string' && value.length > 0

const isObjectMessage = value => value === '[object Object]'

/**
 * @param {unknown} value
 * @returns {string}
 */
export const extractErrorMessage = value => {
  if (value == null) return 'Unknown error'

  if (typeof value === 'string') return value

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  if (value instanceof Error) {
    if (isNonEmptyString(value.message) && !isObjectMessage(value.message)) {
      return value.message
    }

    if (value.data != null) {
      const fromData = extractErrorMessage(value.data)
      if (fromData !== 'Unknown error') return fromData
    }

    if (value.cause != null) {
      const fromCause = extractErrorMessage(value.cause)
      if (fromCause !== 'Unknown error') return fromCause
    }

    return 'Unknown error'
  }

  if (typeof value === 'object') {
    const apiDetail =
      value?.error?.errors?.[0]?.detail ??
      value?.errors?.[0]?.detail

    if (isNonEmptyString(apiDetail)) return apiDetail

    const apiCode =
      value?.error?.errors?.[0]?.code ??
      value?.errors?.[0]?.code

    if (isNonEmptyString(apiCode) && !isNonEmptyString(apiDetail)) return apiCode

    const nestedCandidates = [
      value?.error?.message,
      value?.error?.detail,
      value?.error?.error,
      value?.detail,
      value?.message,
    ]

    for (const candidate of nestedCandidates) {
      if (isNonEmptyString(candidate) && !isObjectMessage(candidate)) {
        return candidate
      }
    }

    if (value?.error && typeof value.error === 'object') {
      const nested = extractErrorMessage(value.error)
      if (nested !== 'Unknown error') return nested
    }

    if (typeof value.error === 'string') return value.error

    if (value?.status != null) {
      return `Request failed with status ${value.status}`
    }

    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }

  return String(value)
}

/**
 * @param {unknown} value
 * @returns {Error}
 */
export const normalizeToError = value => {
  if (value instanceof Error) {
    const message = extractErrorMessage(value)
    if (value.message === message) return value

    const error = new Error(message)
    error.name = value.name || 'Error'
    if (value.stack) error.stack = value.stack
    return error
  }

  const message = extractErrorMessage(value)
  const error = new Error(message)

  if (value && typeof value === 'object' && value.stack) {
    error.stack = value.stack
  }

  return error
}
